import { CachedLearningAudio } from '@shared/media/audio/CachedLearningAudio'
import { useEffect, useRef, useState } from 'react'
import { Volume2 } from 'lucide-react'
import { LmsButton } from '@shared/ui'
import { LearningAudioWave } from '@features/learning/shared/online-book-content/primitives'
import { getNumberedChoiceShortcutIndex } from './learning-template-shortcuts'
import type {
  LearningQuizAnswer,
  LearningQuizTemplateProps,
} from './learning-template-contract'
import './learning-quiz-template.css'

export function LearningQuizTemplate({
  answers,
  fullwidth = false,
  itemId,
  onAnswer,
  oneThird = false,
  result = null,
  selectedAnswerId = null,
  selectedAnswerIndex = null,
  sourceOwner,
}: LearningQuizTemplateProps) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (result) {
        return
      }

      const answerIndex = getNumberedChoiceShortcutIndex(event, answers.length)
      if (answerIndex === null) {
        return
      }

      event.preventDefault()
      onAnswer(answerIndex)
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [answers.length, onAnswer, result])

  return (
    <div
      className="learningTemplateQuiz boxWrapAnswer"
      data-legacy-owner={sourceOwner}
      data-legacy-template="1"
      data-qa="learning-template-1-quiz"
    >
      <div
        className={`learningTemplateQuizAnswers${result ? ' answered' : ''}${
          fullwidth ? ' fullwidth' : oneThird ? ' oneThird' : ''
        }`}
      >
        {answers.map((answer, index) => (
          <LearningQuizAnswerCard
            answer={answer}
            answerIndex={index}
            isAnswered={Boolean(result)}
            key={`${itemId}-answer-${answer.id ?? index}`}
            onAnswer={onAnswer}
            result={
              result &&
              (selectedAnswerId !== null
                ? answer.id === selectedAnswerId
                : index === selectedAnswerIndex)
                ? result
                : null
            }
          />
        ))}
      </div>
    </div>
  )
}

function LearningQuizAnswerCard({
  answer,
  answerIndex,
  isAnswered,
  onAnswer,
  result,
}: {
  readonly answer: LearningQuizAnswer
  readonly answerIndex: number
  readonly isAnswered: boolean
  readonly onAnswer: (answerIndex: number) => void
  readonly result: Exclude<LearningQuizTemplateProps['result'], null | undefined> | null
}) {
  const hasAnswerImage = Boolean(answer.image)
  const hasAnswerAudio = Boolean(answer.audio)
  const answerClassName = `${
    !isAnswered ? ' learningTemplateQuizHasSort' : ''
  }${hasAnswerImage ? ' learningTemplateQuizHasImage' : ''}${
    hasAnswerAudio ? ' learningTemplateQuizHasAudio' : ''
  }${result ? ` ${result}` : ''}`
  const answerBody = (
    <>
      {answer.image ? (
        <span className="learningTemplateQuizAnswerMedia">
          <img alt="" src={answer.image} />
        </span>
      ) : null}
      {answer.label ? (
        <span
          className={`learningTemplateQuizAnswerText${
            hasAnswerImage || hasAnswerAudio ? ' mb-15' : ''
          }`}
        >
          {answer.label}
        </span>
      ) : null}
    </>
  )

  if (answer.audio) {
    return (
      <div
        aria-label={answer.label || `Answer option ${answerIndex + 1} has no published text`}
        className={`learningTemplateQuizAnswerCard${answerClassName}`}
        data-sparse-answer={answer.isSparse ? 'true' : undefined}
        data-suppressed-generic-label={answer.suppressGenericLabel ? 'true' : undefined}
      >
        {answerBody}
        <LearningQuizAnswerAudio result={result} source={answer.audio} />
        <LmsButton
          aria-keyshortcuts={!isAnswered && answerIndex < 9 ? String(answerIndex + 1) : undefined}
          className={`learningTemplateQuizAudioAction${result ? ` ${result}` : ''}`}
          disabled={isAnswered}
          onClick={() => onAnswer(answerIndex)}
          type="button"
          variant="accent"
        >
          Answer {answerIndex + 1}
        </LmsButton>
        {!isAnswered ? <span className="learningTemplateQuizExerciseSort">{answerIndex + 1}</span> : null}
      </div>
    )
  }

  return (
    <LmsButton
      aria-keyshortcuts={!isAnswered && answerIndex < 9 ? String(answerIndex + 1) : undefined}
      aria-label={answer.label || `Answer option ${answerIndex + 1} has no published content`}
      className={`learningTemplateQuizAnswerButton${answerClassName}`}
      data-sparse-answer={answer.isSparse ? 'true' : undefined}
      data-suppressed-generic-label={answer.suppressGenericLabel ? 'true' : undefined}
      disabled={isAnswered}
      onClick={() => onAnswer(answerIndex)}
      type="button"
      variant="ghost"
    >
      {answerBody}
      {!isAnswered ? <span className="learningTemplateQuizExerciseSort">{answerIndex + 1}</span> : null}
    </LmsButton>
  )
}

function LearningQuizAnswerAudio({
  result,
  source,
}: {
  readonly result: Exclude<LearningQuizTemplateProps['result'], null | undefined> | null
  readonly source: string
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const handleTogglePlayback = () => {
    if (!audioRef.current) {
      return
    }

    if (audioRef.current.paused) {
      void audioRef.current.play().catch(() => undefined)
      return
    }

    audioRef.current.pause()
  }

  return (
    <span className={`learningTemplateQuizAnswerAudio${result ? ` ${result}` : ''}`}>
      <LmsButton
        aria-label={isPlaying ? 'Pause answer audio' : 'Play answer audio'}
        aria-pressed={isPlaying}
        className="learningTemplateQuizAnswerAudioButton"
        onClick={handleTogglePlayback}
        type="button"
        variant="ghost"
      >
        {isPlaying ? (
          <LearningAudioWave isPlaying size={24} />
        ) : (
          <Volume2 aria-hidden="true" className="learningTemplateQuizAnswerAudioIdleIcon" />
        )}
      </LmsButton>
      <CachedLearningAudio
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        ref={audioRef}
        src={source}
      />
    </span>
  )
}
